import React, { useEffect, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { useGlobalStates } from "../../utlis/ClubsContext";
import { callApi } from "../../services/ApiServices";
import CommonLoader from "../common/CommonLoader";
import { UserRole } from "../../utlis/constant";
import { ImagePath } from "../common/ImagePath";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "9999px",
    borderColor: "#DADADA",
    boxShadow: "none",
    paddingLeft: "8px",
    "&:hover": { borderColor: "#DADADA" },
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  menu: (base) => ({ ...base, borderRadius: "10px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#f3f4f6" : "#fff",
    color: "#111827",
    cursor: "pointer",
  }),
};

const SuggestionsTab = () => {
  const { selectedClub, userRole } = useGlobalStates();
  const [loading, setLoading] = useState(false);
  const [clubOptions, setClubOptions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);

  const isViewer = userRole === UserRole.Viewer;

  useEffect(() => {
    getClubNames();
  }, []);

  useEffect(() => {
    if (selectedClub?.id) fetchSuggestions();
  }, [selectedClub?.id]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "get",
        url: `admin/suggestion-list/${selectedClub?.id}`,
      });
      if (response.status === 200) {
        const result = response?.data || response;
        const options = (result?.relatedClubs || []).map((club) => ({
          value: club?.id,
          label: club?.clubName,
        }));
        setSuggestions(options);
        setSelected(options);
      }
    } catch (error) {
      console.log("error while fetching suggestions", error);
    } finally {
      setLoading(false);
    }
  };

  const getClubNames = async () => {
    try {
      const response = await callApi({
        method: "get",
        url: "admin/club-name",
      });
      const options = response?.data?.map((club) => ({
        value: club?.id,
        label: club?.clubName,
      }));
      setClubOptions(options || []);
    } catch (error) {
      console.error("Error fetching club Names:", error);
    }
  };

  const saveSuggestions = async (clubs) => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "post",
        url: `admin/related-club/${selectedClub?.id}`,
        data: {
          clubIds: clubs.map((club) => club.value),
        },
      });
      if (response.status === 200 || response.status === 201) {
        fetchSuggestions();
        toast.success(response?.message || "Suggestions updated", {
          id: "suggestionsToast",
        });
      }
    } catch (error) {
      console.log("error while saving suggestions", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (clubId) => {
    saveSuggestions(suggestions.filter((club) => club.value !== clubId));
  };

  const availableOptions = clubOptions.filter(
    (option) => option.value !== selectedClub?.id,
  );

  return (
    <>
      {loading && <CommonLoader fullScreen />}

      {/* Header Card */}
      <div className="bg-white rounded-xl p-6 main-shadow mb-6">
        <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
          Suggestions
        </h2>
        <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px] mb-4">
          Clubs suggested to advertisers alongside this club.
        </p>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1">
            <Select
              isMulti
              options={availableOptions}
              value={selected}
              onChange={(options) => setSelected(options || [])}
              styles={selectStyles}
              placeholder="Select clubs"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              closeMenuOnSelect={false}
              isDisabled={isViewer}
              isClearable
            />
          </div>
          <button
            onClick={() => saveSuggestions(selected)}
            disabled={isViewer || loading}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors
              ${
                isViewer
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                  : "bg-primary hover:bg-hover text-white cursor-pointer"
              }`}
          >
            Save
          </button>
        </div>
      </div>

      {/* Suggestions List */}
      {suggestions.length === 0 ? (
        <div className="bg-white rounded-xl p-12 main-shadow flex flex-col items-center justify-center text-center">
          <div className="flex justify-center items-center">
            <img
              src={ImagePath?.NoDataFound}
              alt="No Data Found"
              style={{ width: 60, height: 60 }}
            />
          </div>
          <p className="text-medium mt-2">No Data found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {suggestions.map((club) => (
            <div
              key={club.value}
              className="bg-white rounded-xl p-5 main-shadow flex items-center justify-between gap-4"
            >
              <p className="text-md font-semibold text-black">{club.label}</p>
              {!isViewer && (
                <button
                  onClick={() => handleRemove(club.value)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                  title="Remove"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SuggestionsTab;
